import {
  Button,
  Dialog, DialogContent,
  DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui'
import { useWorkflowRunNodeContent } from '@/hooks'
import type { WorkflowRunNode } from '@/types'
import { RotateCw } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

interface Props {
  node: WorkflowRunNode | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (nodeId: string, modifiedInputsJson?: string) => void
  isPending?: boolean
}

export function RetryNodeDialog({ node, open, onOpenChange, onConfirm, isPending }: Props) {
  const { projectId, runId } = useParams()
  const content = useWorkflowRunNodeContent(projectId!, runId!, node)
  const [inputsJson, setInputsJson] = useState<string | null>(null)
  const [showInputs, setShowInputs] = useState(false)

  if (!node) return null

  const toggleInputs = () => {
    if (!showInputs && inputsJson === null) {
      setInputsJson(JSON.stringify(content.data?.inputValues ?? {}, null, 2))
    }
    setShowInputs(!showInputs)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-button">
            <RotateCw className="h-4 w-4" /> Retry Node
          </DialogTitle>
          <DialogDescription>
            Retry &ldquo;{node.name}&rdquo;? This will reset the node and re-execute it.
          </DialogDescription>
        </DialogHeader>

        {node.errorMessage && (
          <div className="text-xs text-danger-foreground bg-danger p-3 rounded border border-danger-foreground">
            <span className="font-medium">Last error:</span> {node.errorMessage}
          </div>
        )}

        <div>
          {content.isError && !content.data ? (
            <div role="alert" className="flex items-center gap-2 text-xs text-danger-foreground">
              Couldn't load the node's input values.
              <button type="button" className="underline" onClick={() => content.refetch()}>
                Try again
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="text-xs text-muted-foreground underline disabled:no-underline disabled:opacity-60"
              disabled={!content.data}
              onClick={toggleInputs}
            >
              {content.data ? `${showInputs ? 'Hide' : 'Modify'} input values` : 'Loading input values…'}
            </button>
          )}
          {showInputs && (
            <textarea
              className="mt-2 w-full h-40 font-mono text-xs bg-muted p-3 rounded border resize-none"
              aria-label="Input values (JSON)"
              value={inputsJson ?? ''}
              onChange={e => setInputsJson(e.target.value)}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-button hover:bg-button/90"
            disabled={isPending}
            onClick={() => {
              const modified = showInputs ? inputsJson ?? undefined : undefined
              onConfirm(node.id, modified)
              onOpenChange(false)
            }}
          >
            <RotateCw className="h-3 w-3 mr-1" /> Retry Node
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
